import React from 'react';

interface LastModifiedIndicatorProps {
  lastModified: Date | string | null;
  className?: string;
  showRelativeTime?: boolean;
  showTooltip?: boolean;
  modifiedBy?: string;
}

/**
 * Componente para exibir indicador visual de última modificação
 * Mostra tempo relativo (ex: "há 2 horas") com cores indicativas
 */
const LastModifiedIndicator: React.FC<LastModifiedIndicatorProps> = ({
  lastModified,
  className = '',
  showRelativeTime = true,
  showTooltip = true,
  modifiedBy,
}) => {
  // Se não há data de modificação válida, não mostrar nada
  if (!lastModified) {
    return null;
  }

  const lastModifiedDate = new Date(lastModified);
  // Verificar se a data é válida
  if (isNaN(lastModifiedDate.getTime())) {
    return null;
  }

  const now = new Date();
  const diffMs = now.getTime() - lastModifiedDate.getTime();
  // Garantir que diffMs é um número válido
  if (isNaN(diffMs) || diffMs < 0) {
    return null;
  }

  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);

  // Determinar cor baseada no tempo desde a modificação
  const getColorClass = () => {
    if (diffHours < 1) return 'bg-green-100 text-green-800 border-green-200'; // < 1 hora
    if (diffHours < 24) return 'bg-blue-100 text-blue-800 border-blue-200';   // < 24 horas
    if (diffDays < 7) return 'bg-yellow-100 text-yellow-800 border-yellow-200'; // < 7 dias
    return 'bg-gray-100 text-gray-600 border-gray-200'; // > 7 dias
  };

  // Determinar ícone baseado no tempo
  const getIcon = () => {
    if (diffHours < 1) return '🟢'; // Verde para muito recente
    if (diffHours < 24) return '🔵'; // Azul para recente
    if (diffDays < 7) return '🟡'; // Amarelo para moderado
    return '⚪'; // Cinza para antigo
  };

  // Formatar tempo relativo
  const formatRelativeTime = () => {
    if (diffHours < 1) {
      const minutes = Math.floor(diffMs / (1000 * 60));
      return minutes < 1 ? 'agora' : `há ${minutes} min`;
    }
    if (diffHours < 24) {
      return `há ${diffHours} h`;
    }
    if (diffDays < 7) {
      return `há ${diffDays} dia${diffDays > 1 ? 's' : ''}`;
    }
    if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return `há ${weeks} semana${weeks > 1 ? 's' : ''}`;
    }
    return `há ${Math.floor(diffDays / 30)} mês${Math.floor(diffDays / 30) > 1 ? 'es' : ''}`;
  };

  // Formatar data completa para tooltip
  const formatFullDate = () => {
    return lastModifiedDate.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  // Construir texto do tooltip
  const getTooltipText = () => {
    let text = `Modificado em: ${formatFullDate()}`;
    if (modifiedBy) {
      text += `\nPor: ${modifiedBy}`;
    }
    return text;
  };

  const colorClass = getColorClass();
  const icon = getIcon();
  const relativeTime = formatRelativeTime();

  return (
    <div className={`inline-flex items-center gap-1 ${className}`}>
      {showTooltip ? (
        <div className="relative group">
          <span
            className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${colorClass}`}
          >
            <span className="text-xs">{icon}</span>
            {showRelativeTime && <span>{relativeTime}</span>}
          </span>
          <div className="absolute z-10 invisible group-hover:visible bg-gray-900 text-white text-xs rounded px-2 py-1 -top-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
            {getTooltipText()}
            <div className="absolute bottom-[-4px] left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-2 border-r-2 border-t-2 border-l-transparent border-r-transparent border-t-gray-900"></div>
          </div>
        </div>
      ) : (
        <span
          className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${colorClass}`}
        >
          <span className="text-xs">{icon}</span>
          {showRelativeTime && <span>{relativeTime}</span>}
        </span>
      )}
    </div>
  );
};

export default LastModifiedIndicator;